import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import { useToast } from '@/hooks/use-toast';
import type { LanguageCode } from '@/lib/i18n/registry';

/**
 * Accessible language selector. Native names only. Hidden when a single
 * language is available (production today: English only).
 */
export function LanguageSelector({ className }: { className?: string }) {
  const { t, i18n } = useTranslation('common');
  const { current, options, setLanguage } = useLanguage();
  const { toast } = useToast();

  if (options.length < 2) return null;

  return (
    <label className={`flex items-center gap-3 px-4 py-3 ${className ?? ''}`}>
      <Languages className="h-4 w-4 text-muted-foreground" aria-hidden />
      <span className="flex-1 text-sm text-foreground">{t('language.label')}</span>
      <select
        aria-label={t('language.label')}
        value={current}
        onChange={async (e) => {
          const { error } = await setLanguage(e.target.value as LanguageCode);
          const tt = i18n.getFixedT(null, 'common');
          toast(error ? { title: tt('language.saveFailed'), variant: 'destructive' } : { title: tt('language.changed') });
        }}
        className="rounded-md border border-border/40 bg-card px-2 py-1 text-sm text-foreground"
      >
        {options.map((l) => (
          <option key={l.code} value={l.code} lang={l.code}>
            {l.nativeName}
            {l.status === 'review' ? ` (${t('language.reviewBadge')})` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
